import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  X, ChevronLeft, ChevronRight, Sparkles,
  ChefHat, Wine, Sofa, Tv, Layers, Trophy,
  BedDouble, Bed, ShowerHead, Bath, Droplets, WashingMachine,
} from "lucide-react";
import { api } from "@/lib/sleek";

// "Tour every room" room-jump map per project id.
// Each entry → label, icon, and 0-based index of the photo to jump to in the gallery.
const ROOM_TOURS = {
  p40: [
    { label: "Kitchen & Dining", icon: ChefHat, idx: 0 },
    { label: "Butler's Pantry", icon: Wine, idx: 2 },
    { label: "Powder Room", icon: Droplets, idx: 3 },
    { label: "Hall Bath", icon: Bath, idx: 4 },
    { label: "Living Room", icon: Sofa, idx: 5 },
    { label: "Family Room", icon: Tv, idx: 8 },
    { label: "Lower-Level Den", icon: Layers, idx: 7 },
    { label: "Game Room", icon: Trophy, idx: 9 },
    { label: "Primary Suite", icon: BedDouble, idx: 10 },
    { label: "Spa Ensuite", icon: ShowerHead, idx: 13 },
    { label: "Guest Bedrooms", icon: Bed, idx: 16 },
    { label: "Basement Bath", icon: Droplets, idx: 20 },
    { label: "Laundry Room", icon: WashingMachine, idx: 22 },
  ],
};

export default function PortfolioDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [active, setActive] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState(-1); // -1 = closed
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProject(null);
    setActive(0);
    setLightboxIdx(-1);
    setNotFound(false);
    api
      .get(`/portfolio/${id}`)
      .then((r) => setProject(r.data))
      .catch(() => setNotFound(true));

    // Track view (dedupe per browser session per project)
    try {
      const key = `sleek_viewed_${id}`;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        api.post(`/portfolio/${id}/view`).catch(() => {});
      }
    } catch (_e) {
      // sessionStorage may be unavailable (private mode) — just skip dedupe
      api.post(`/portfolio/${id}/view`).catch(() => {});
    }
  }, [id]);

  const gallery = project?.gallery?.length ? project.gallery : project ? [project.image] : [];
  const lbOpen = lightboxIdx >= 0;

  const closeLb = useCallback(() => setLightboxIdx(-1), []);
  const prevLb = useCallback(
    () => setLightboxIdx((i) => (i <= 0 ? gallery.length - 1 : i - 1)),
    [gallery.length],
  );
  const nextLb = useCallback(
    () => setLightboxIdx((i) => (i >= gallery.length - 1 ? 0 : i + 1)),
    [gallery.length],
  );

  // Lock body scroll + keyboard nav while lightbox is open
  useEffect(() => {
    if (!lbOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLb();
      else if (e.key === "ArrowLeft") prevLb();
      else if (e.key === "ArrowRight") nextLb();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [lbOpen, closeLb, prevLb, nextLb]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-[1200px] px-6 md:px-12 lg:px-16 py-32 text-center">
        <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">404</div>
        <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight mt-4">
          Project not found.
        </h1>
        <Link
          to="/portfolio"
          className="inline-block mt-10 bg-zinc-950 text-white px-7 py-4 text-sm hover:bg-zinc-800 transition"
          data-testid="detail-back-to-portfolio"
        >
          Back to Portfolio
        </Link>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-32">
        <div className="text-zinc-400 text-sm tracking-[0.25em] uppercase">Loading…</div>
      </div>
    );
  }

  const heroImg = gallery[active] || project.image;

  return (
    <div
      className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-12 md:py-20"
      data-testid={`detail-${project.id}`}
    >
      {/* Back nav */}
      <button
        onClick={() => navigate(-1)}
        className="text-xs tracking-[0.25em] uppercase text-zinc-500 hover:text-zinc-950 transition"
        data-testid="detail-back"
      >
        ← Portfolio
      </button>

      {/* Title block */}
      <div className="mt-6 md:mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
        <div className="lg:col-span-7">
          <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">{project.category}</div>
          <h1 className="font-display text-4xl md:text-6xl font-light tracking-tight mt-3 leading-[1.05]">
            {project.title}
          </h1>
        </div>
        <div className="lg:col-span-5 lg:pt-2">
          <p className="text-zinc-600 leading-relaxed">{project.summary}</p>
          <div className="flex flex-wrap gap-2 mt-5">
            {(project.tags || []).map((t) => (
              <span
                key={t}
                className="text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 border border-zinc-300 text-zinc-700"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Hero image — click to open lightbox */}
      <button
        type="button"
        onClick={() => setLightboxIdx(active)}
        className="block w-full mt-10 md:mt-14 overflow-hidden bg-zinc-100 group"
        aria-label="Open full-size view"
        data-testid="detail-hero-open"
      >
        <img
          src={heroImg}
          alt={project.title}
          className="w-full h-auto object-cover max-h-[78vh] group-hover:opacity-95 transition cursor-zoom-in"
          data-testid="detail-hero-img"
        />
      </button>

      {/* "Tour every room" — room-jump chips (only for projects with a defined tour map) */}
      {ROOM_TOURS[id] && gallery.length > 1 && (
        <div className="mt-10" data-testid="room-tour">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-4">
            Tour every room · jump straight into the lightbox
          </div>
          <div className="flex flex-wrap gap-2">
            {ROOM_TOURS[id]
              .filter((r) => r.idx < gallery.length)
              .map((r) => {
                const Icon = r.icon;
                return (
                  <button
                    key={r.label}
                    type="button"
                    onClick={() => {
                      setActive(r.idx);
                      setLightboxIdx(r.idx);
                    }}
                    className="group inline-flex items-center gap-2 px-4 py-2 border border-zinc-300 text-zinc-700 text-xs tracking-wide uppercase hover:bg-zinc-950 hover:text-white hover:border-zinc-950 transition"
                    data-testid={`room-tour-chip-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    aria-label={`Tour ${r.label} — opens photo ${r.idx + 1}`}
                  >
                    <Icon className="w-4 h-4 transition-transform group-hover:scale-110" strokeWidth={1.5} />
                    <span>{r.label}</span>
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {/* Thumbnail strip — updates hero AND opens lightbox; never scrolls page */}
      {gallery.length > 1 && (
        <div className="mt-8">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-4">
            Gallery — {gallery.length} photos · click any for full view
          </div>
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3">
            {gallery.map((src, idx) => (
              <button
                key={src}
                type="button"
                onClick={() => {
                  setActive(idx);
                  setLightboxIdx(idx);
                }}
                className={`aspect-square overflow-hidden border-2 transition ${
                  idx === active ? "border-zinc-950" : "border-transparent hover:border-zinc-400"
                }`}
                data-testid={`detail-thumb-${idx}`}
                aria-label={`Open photo ${idx + 1} of ${gallery.length}`}
              >
                <img
                  src={src}
                  alt={`${project.title} — view ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="border-t border-zinc-200 mt-20 pt-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="font-display text-3xl md:text-4xl font-light">
            Want a project like this?
          </div>
          <p className="text-zinc-600 mt-2 max-w-xl">
            Tell us what you&apos;ve been picturing. We&apos;re here to help you complete your new project!
          </p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/portfolio"
            className="border border-zinc-300 text-zinc-900 px-7 py-4 text-sm hover:border-zinc-950 transition w-fit"
            data-testid="detail-more-projects"
          >
            More projects
          </Link>
          <Link
            to="/booking"
            className="bg-zinc-950 text-white px-7 py-4 text-sm hover:bg-zinc-800 transition w-fit"
            data-testid="detail-book-cta"
          >
            Book a consultation
          </Link>
        </div>
      </div>

      {/* Lightbox */}
      {lbOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-label={`${project.title} — photo ${lightboxIdx + 1} of ${gallery.length}`}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeLb();
          }}
          data-testid="lightbox"
        >
          {/* Close */}
          <button
            type="button"
            onClick={closeLb}
            className="absolute top-5 right-5 md:top-7 md:right-7 text-white/80 hover:text-white p-2"
            aria-label="Close gallery"
            data-testid="lightbox-close"
          >
            <X size={28} />
          </button>

          {/* Counter */}
          <div
            className="absolute top-6 left-1/2 -translate-x-1/2 text-white/70 text-xs tracking-[0.3em] uppercase"
            data-testid="lightbox-counter"
          >
            {lightboxIdx + 1} / {gallery.length}
          </div>

          {/* Prev / Next */}
          {gallery.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prevLb(); }}
                className="absolute left-3 md:left-6 text-white/70 hover:text-white p-3 md:p-4"
                aria-label="Previous photo"
                data-testid="lightbox-prev"
              >
                <ChevronLeft size={36} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); nextLb(); }}
                className="absolute right-3 md:right-6 text-white/70 hover:text-white p-3 md:p-4"
                aria-label="Next photo"
                data-testid="lightbox-next"
              >
                <ChevronRight size={36} />
              </button>
            </>
          )}

          {/* Image */}
          <img
            src={gallery[lightboxIdx]}
            alt={`${project.title} — view ${lightboxIdx + 1}`}
            className="max-w-[92vw] max-h-[88vh] object-contain select-none"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-img"
          />

          {/* Caption + Get-this-look CTA */}
          <div className="absolute bottom-0 left-0 right-0 px-6 pb-5 md:pb-6 flex items-center justify-between gap-4">
            <div className="text-white/75 text-xs tracking-[0.25em] uppercase truncate">
              {project.title}
            </div>
            <Link
              to={`/booking?ref=${encodeURIComponent(project.id)}&title=${encodeURIComponent(project.title)}&cat=${encodeURIComponent(project.category)}`}
              onClick={(e) => e.stopPropagation()}
              className="group inline-flex items-center gap-2 bg-white text-zinc-950 px-4 md:px-5 py-2.5 md:py-3 text-xs tracking-[0.2em] uppercase hover:bg-zinc-200 transition whitespace-nowrap"
              data-testid="lightbox-get-this-look"
            >
              <Sparkles size={14} className="opacity-70 group-hover:opacity-100" />
              Get this look
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
